import React, { useState, useEffect, useCallback } from 'react';
// toastify
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// material
import { Box, CircularProgress } from '@mui/material';

// api
import { useDispatch } from 'react-redux';
import { apiMain } from './api/config/axios';

// routes
import Router from './routes';

// theme
import ThemeProvider from './theme';
// components
import ScrollToTop from './components/ScrollToTop';
import { BaseOptionChartStyle } from './components/chart/BaseOptionChart';
// pages
import BootstrapError from './pages/BootstrapError';
import { SET_ACTIVE_APP } from './store/constants/auth';

// ----------------------------------------------------------------------

export default function App() {
  const dispatch = useDispatch();
  const [isReady, setIsReady] = useState(false);
  const [bootstrapError, setBootstrapError] = useState(null);

  const bootstrap = useCallback(async () => {
    setIsReady(false);
    const { hostname } = window.location;
    const mainHubDomains = ['app.alisbuild.com', 'localhost'];

    // If we are on the main hub, we do not need to bootstrap a whitelabel app
    if (mainHubDomains.some((domain) => hostname.includes(domain))) {
      setBootstrapError(null);
      setIsReady(true);
      return;
    }

    try {
      const response = await apiMain.get(`/api/v1/user/bootstrap`, { domain: hostname });
      if (response.ok && response.data?.success) {
        const app = response.data.data;
        sessionStorage.setItem('bootstrappedApp', JSON.stringify(app));
        dispatch({ type: SET_ACTIVE_APP, payload: app });

        // Set Title dynamically
        document.title = app.name || 'Admin Panel';

        // Set Favicon dynamically if provided
        if (app.branding?.faviconUrl) {
          const link = document.querySelector("link[rel~='icon']") || document.createElement('link');
          link.type = 'image/x-icon';
          link.rel = 'shortcut icon';
          link.href = app.branding.faviconUrl;
          document.getElementsByTagName('head')[0].appendChild(link);
        }
        setBootstrapError(null);
      } else {
        const { status, data } = response;
        const message = data?.message || '';
        const isNotFound = status === 400 || message.toLowerCase().includes('not found');
        setBootstrapError({
          type: isNotFound ? 'NOT_FOUND' : 'CONNECTION_FAILED',
          message: message || 'Failed to retrieve application configuration.',
        });
      }
    } catch (err) {
      console.error('Failed to bootstrap app:', err);
      setBootstrapError({
        type: 'CONNECTION_FAILED',
        message: 'A network error occurred while establishing connection.',
      });
    } finally {
      setIsReady(true);
    }
  }, []);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  if (!isReady) {
    return (
      <Box
        sx={{
          display: 'flex',
          minHeight: '100vh',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#fbfbfd',
        }}
      >
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (bootstrapError) {
    return (
      <ThemeProvider>
        <BootstrapError error={bootstrapError} onRetry={bootstrap} />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <ToastContainer
        position="top-center"
        autoClose={2000}
        hideProgressBar
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover={false}
      />
      <ScrollToTop />
      <BaseOptionChartStyle />
      <Router />
    </ThemeProvider>
  );
}
